import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import couponsService from "@/services/coupons.service";
import { TProductCoupon } from "@/models/products.model";

type CouponsState = {
  initialized: boolean;
  status: TRequestStatus;
  coupons: TProductCoupon[];
};

type CouponsActions = {
  getAllCoupons: () => Promise<TProductCoupon[]>;
  saveCoupon: (data: Omit<TProductCoupon, "created" | "updated">) => Promise<TProductCoupon>;
  updateCoupon: (id: number, data: Omit<TProductCoupon, "created" | "updated">) => Promise<void>;
  removeCoupon: (couponId: number) => Promise<void>;
};

export const useCouponsStore = create<CouponsState & CouponsActions>()(
  devtools(
    (set, get) => ({
      initialized: false,
      status: "IDLE",
      coupons: [],

      getAllCoupons: async () => {
        const { coupons, initialized } = get();
        if (initialized) return coupons;

        set({ status: "PENDING" }, false, "getAllCoupons/pending");
        const response = await couponsService.getCoupons();
        set(
          { coupons: response, status: "IDLE", initialized: true },
          false,
          "getAllCoupons/fulfilled"
        );
        return response;
      },

      saveCoupon: async (data) => {
        const response = await couponsService.saveCoupon(data);
        set(
          (state) => ({ coupons: [...state.coupons, response] }),
          false,
          "saveCoupon/fulfilled"
        );
        return response;
      },

      updateCoupon: async (id, data) => {
        const response = await couponsService.updateCoupon(id, data);
        set(
          (state) => ({
            coupons: state.coupons.map((t) => (t.id === response.id ? response : t)),
          }),
          false,
          "updateCoupon/fulfilled"
        );
      },

      // Optimistic removal: drop the coupon immediately, restore on API failure.
      removeCoupon: async (couponId) => {
        const couponsForRollback = get().coupons;
        set(
          (state) => ({ coupons: state.coupons.filter((t) => t.id !== couponId) }),
          false,
          "removeCoupon/optimistic"
        );

        try {
          await couponsService.deleteCoupon(couponId);
        } catch (error) {
          set(
            { coupons: couponsForRollback, status: "IDLE" },
            false,
            "removeCoupon/rollback"
          );
          throw error;
        }
      },
    }),
    { name: "coupons" }
  )
);
